<template>
  <b-card class="m-3 msm-card p-0 text-center" style="width: 236px">
    <b-row style="min-height: 512px">
      <b-col cols="12" align-self="start">
        <span
          v-if="(cardData.Image || '').length == 2"
          style="height: 137px; box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 8px 0px"
          :class="'flag-icon flag-icon-' + cardData.Image.toLowerCase()"
        ></span>

        <b-img-lazy
          v-else
          :src="cardData.Image || defaultLogo"
          :onerror="`this.onerror = null; this.src = '${defaultLogo}';`"
          class="card-image"
          fluid
          style="max-height: 124px; max-width: 100%"
        />
      </b-col>

      <b-col cols="12" align-self="center">
        <p class="msm-card-title line-clamp sm mb-0">{{ cardData.Title }}</p>
      </b-col>

      <b-col class="msm-card-end" cols="12" align-self="end" >
        <small class="msm-card-subtitle line-clamp sm mb-0" v-html="cardData.AdditionalInfo"></small>
        <p class="msm-card-subtitle mb-0" v-if="cardData.Subtitle != ''">
          ({{ cardData.Subtitle }})
        </p>
        <div ref="box">
          <DxCircularGauge
                  id="gauge"
                  class="circular-gauge"
                  :value="cardData.Value"
                  @change="beautifyGauge"
                >
            <DxSize :height="170" :width="gaugeWidth"/>
            <DxMargin :top="0" :bottom="0" :left="0" :right="0" />
            <DxScale :start-value="0" :end-value="100" :tick-interval="100">
              <DxLabel :customize-text="customizeText" />
            </DxScale>
            <DxGeometry :start-angle="180" :end-angle="360" />
            <DxRangeContainer>
              <DxRange
                v-for="(thres, index) in colorRange"
                :key="index"
                :start-value="thres.Minimum"
                :end-value="thres.Maximum - 1"
                :color="thres.Color"
              />
            </DxRangeContainer>
            <DxTitle
              :text="(cardData.Value != null ? `${cardData.Value} %` : `N/A` )"
              vertical-alignment="bottom"
            >
              <DxFont
                :size="32"
                :color="determineValueColor(cardData.Value)"
                :weight="600"
              />
            </DxTitle>
            <DxValueIndicator type="triangleMarker" color="black" />
          </DxCircularGauge>
        </div>
        <p class="m-0 msm-overall">{{ $t("Webportal.CX.OverallScore") }}</p>
        <p class="mb-0" v-if="showActionsToTake">
          <b>{{ cardData.ActionsToTake }}</b>
          {{ $t("Webportal.CX.ActionsToTake") }}
        </p>
      </b-col>
    </b-row>

    <template v-slot:footer>
      <b-button @click="drilldownAction" class="msm-drilldown m-0 p-0">
        {{ $t("Webportal.CX.Drilldown") }}
        <font-awesome-icon :icon="['far', 'plus']" />
      </b-button>
    </template>
  </b-card>
</template>

<script>
import {
  DxCircularGauge,
  DxScale,
  DxSize,
  DxLabel,
  DxRangeContainer,
  DxRange,
  DxTitle,
  DxFont,
  DxExport,
  DxValueIndicator,
  DxGeometry,
  DxMargin,
} from "devextreme-vue/circular-gauge";

export default {
  name: "Card",

  components: {
    DxCircularGauge,
    DxScale,
    DxSize,
    DxLabel,
    DxRangeContainer,
    DxRange,
    DxTitle,
    DxFont,
    DxExport,
    DxValueIndicator,
    DxGeometry,
    DxMargin,
  },
  props: {
    colorRange: Array,
    defaultLogo: String,
    cardData: {
      type: Object,
      default: () => {},
    },
    showActionsToTake: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      gaugeWidth: 0
    };
  },
  updated() {
    this.beautifyGauge();
  },
  mounted: function () {
    this.gaugeWidth = this.$refs["box"].clientWidth;
    window.addEventListener("resize", this.handleResize);
    this.$nextTick(function () {
      this.beautifyGauge();
    });
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    customizeText({ valueText }) {
      return `${valueText} %`;
    },
    drilldownAction() {
      this.$emit("DrilldownAction", this.cardData.Drilldown);
    },
    determineValueColor(value) {
      if (!this.colorRange) return null;

      var x = this.colorRange.find(
        (thres) => thres.Minimum <= value && thres.Maximum >= value
      )?.Color;
    },
    handleResize() {
      setTimeout(() => {
        this.resized = true;
        this.beautifyGauge();
      }, 200);
    },
    copyPathsToClipPath(paths = []) {
      let clipPath = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "mask"
      );
      clipPath.setAttribute("id", "clippath-blur");

      let clipPathPaths = [];
      paths.forEach((path, index) => {
        clipPathPaths.push(
          document.createElementNS("http://www.w3.org/2000/svg", "path")
        );

        clipPathPaths[index].setAttribute("d", path.getAttribute("d"));
        clipPathPaths[index].setAttribute("stroke-width", 7);
        clipPathPaths[index].setAttribute("stroke", "white");
        clipPathPaths[index].setAttribute("fill", "white");
        clipPathPaths[index].setAttribute("stroke-linejoin", "bevel");
      });

      clipPathPaths.forEach((path, index) => {
        clipPath.appendChild(path);
      });

      return clipPath;
    },
    beautifyGauge() {
      // -------- Blur effect ---------
      let filters = this.$el.querySelector("svg defs");
      if (filters == null) {
        return -1;
      }
      let newFilter = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "filter"
      );
      newFilter.setAttribute("id", "blur");

      let blur = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "feGaussianBlur"
      );
      blur.setAttribute("stdDeviation", "2");

      newFilter.appendChild(blur);

      filters.appendChild(newFilter);

      let gauges = this.$el.querySelectorAll(".dxg-range-container path");
      gauges.forEach((el) => {
        let color = el.getAttribute("fill");

        if (color != "#808080") {
          el.setAttribute("stroke", color);
          el.setAttribute("fill", color);

          el.setAttribute("stroke-width", 8);

          el.setAttribute("stroke-linejoin", "bevel");
        } else {
          let strokeColor = document.createAttribute("stroke");
          strokeColor.value = "#ffffff";
          el.setAttributeNode(strokeColor);

          let strokeWidth = document.createAttribute("stroke-width");
          strokeWidth.value = 8;
          el.setAttributeNode(strokeWidth);

          el.remove();
        }
      });

      if (!this.$el.querySelector("#clippath-blur") || this.resized) {
        if (this.$el.querySelector("#clippath-blur")) {
          this.$el.querySelector("#clippath-blur").remove();
        }
        filters.appendChild(this.copyPathsToClipPath(gauges));
        let g = this.$el.querySelectorAll(".dxg-range-container");
        g.forEach((el) => {
          el.setAttribute("filter", "url(#blur)");
          el.setAttribute("mask", "url(#clippath-blur)");
        });
      }

      let endlines = this.$el.querySelectorAll(".dxg-line path");
      endlines.forEach((el) => {
        el.remove();
      });

      let indicators = this.$el.querySelectorAll(".dxg-tracker path");
      indicators.forEach((indicator) => {
        let transformBefore = indicator.getAttribute("transform");
        transformBefore = transformBefore + " scale(2)";
        indicator.setAttribute("transform", transformBefore);
      });
    },
  },
};
</script>

<style scoped>
.line-clamp {
  text-overflow: ellipsis;
  overflow: hidden;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.msm-card {
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
  max-width: 20rem;
  min-width: 10rem;
  transition: all 0.02s ease-in-out;
}
.msm-card:hover {
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 5px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
  /* transform: scale(102%, 102%); */
}
.msm-card-title {
  padding-bottom: 0px;
  padding-top: 0px;
  font-size: 1.25rem;
  font-weight: 500;
  min-height: 60px;
  text-align: center;
  display: inline;
}
.msm-card-subtitle {
  color: grey;
}
.msm-card-end {
  height: 300px;
  border-top: 1px solid lightgray;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.msm-drilldown {
  width: 100%;
  background-color: #ffffff;
  border: none;
  color: grey;
}
.msm-overall {
  font-size: 1.75rem;
}
.card-footer {
  padding: 0px;
}
.flag-icon {
  width: 100%;
  background-size: cover;
}
.columnChart {
  height: 100px;
}
.card-body {
  padding-bottom: 0rem;
}
.card-image {
  aspect-ratio: 4/3;
  overflow: hidden;
  object-fit: cover;
  width:100%;
  height:100%;
  object-fit: cover;
  overflow: hidden;
}
</style>
